import Immutable from 'immutable';
import { AbilityType } from '../../api/DnD/abilities';
import { getAbilityMod } from '../../api/DnD/abilityScoreCalculator';
import { DiceType } from '../../api/DnD/dices';
import { getRaceModificator } from '../../api/DnD/races';
import { DiceAction } from '../dices/reducer';
import { PanelType } from '../panels/types';
import {
  AppDispatch,
  AppState,
  AppThunkAction,
  MutableThunkState,
  RollResult,
  RollSource,
  TutorialStep,
} from '../state';
import { TutorialAction } from './reducer';

const LOG_TARGET = 5;
const ROPE_TARGET = 5;
const TUTORIAL_HIT_TARGET = 15;
const TUTORIAL_ENEMY_MAX_HP = 3;
const VICTORY_ADVANCE_DELAY_MS = 650;

const tutorialSingleD20Selection = Immutable.Map<DiceType, number>().set(DiceType.d20, 1);
const tutorialSingleD6Selection = Immutable.Map<DiceType, number>().set(DiceType.d6, 1);

const setTutorialRollModifier =
  (rollModifier: AbilityType | undefined): AppThunkAction =>
  (dispatch: AppDispatch): void => {
    dispatch(DiceAction.setResetAbilityOnNextRoll(false));
    dispatch(DiceAction.setRollModifier(rollModifier));
  };

const persistStep = (settingsStorage: MutableThunkState['settingsStorage'], step: TutorialStep): void => {
  settingsStorage.setTutorialStep(step);
};

const setStep =
  (step: TutorialStep): AppThunkAction =>
  (dispatch: AppDispatch, _getState: () => AppState, { settingsStorage }: MutableThunkState): void => {
    dispatch(TutorialAction.setStep(step));
    persistStep(settingsStorage, step);
  };

const setAttemptsUsed =
  (attemptsUsed: number): AppThunkAction =>
  (dispatch: AppDispatch): void => {
    dispatch(TutorialAction.setAttemptsUsed(attemptsUsed));
  };

const setEncounterState =
  (
    encounterState: Partial<
      Pick<
        AppState['tutorial'],
        'checkOutcome' | 'lastCheckTotal' | 'enemyHp' | 'combatPhase' | 'combatOutcome' | 'lastCombatRoll'
      >
    >,
  ): AppThunkAction =>
  (dispatch: AppDispatch): void => {
    dispatch(TutorialAction.setEncounterState(encounterState));
  };

const resetEncounterState = (): AppThunkAction =>
  setEncounterState({
    checkOutcome: 'idle',
    lastCheckTotal: undefined,
    enemyHp: TUTORIAL_ENEMY_MAX_HP,
    combatPhase: 'aim',
    combatOutcome: 'idle',
    lastCombatRoll: undefined,
  });

const resetDiceForTutorial = (): AppThunkAction =>
  (dispatch: AppDispatch): void => {
    dispatch(DiceAction.setDicesToRoll(tutorialSingleD20Selection));
    dispatch(DiceAction.setRollResults(undefined));
    dispatch(DiceAction.setRollModifier(undefined));
    dispatch(DiceAction.setResultModifier(undefined));
    dispatch(DiceAction.setRollType('normal'));
  };

const skipTutorial =
  (): AppThunkAction =>
  (dispatch: AppDispatch, _getState: () => AppState, { settingsStorage }: MutableThunkState): void => {
    dispatch(TutorialAction.setEnabled(false));
    dispatch(TutorialAction.setCompleted(false));
    dispatch(TutorialAction.setDismissed(true));
    dispatch(TutorialAction.setStep('completed'));
    dispatch(TutorialAction.setAttemptsUsed(0));
    dispatch(resetEncounterState());

    settingsStorage.setTutorialCompleted(false);
    settingsStorage.setTutorialDismissed(true);
    persistStep(settingsStorage, 'completed');
  };

const restartTutorial =
  (): AppThunkAction =>
  (dispatch: AppDispatch, _getState: () => AppState, { settingsStorage }: MutableThunkState): void => {
    dispatch(TutorialAction.setEnabled(true));
    dispatch(TutorialAction.setCompleted(false));
    dispatch(TutorialAction.setDismissed(false));
    dispatch(TutorialAction.setStep('tap_to_roll'));
    dispatch(TutorialAction.setAttemptsUsed(0));
    dispatch(resetDiceForTutorial());
    dispatch(resetEncounterState());

    settingsStorage.setTutorialCompleted(false);
    settingsStorage.setTutorialDismissed(false);
    persistStep(settingsStorage, 'tap_to_roll');
  };

const completeTutorial =
  (): AppThunkAction =>
  (dispatch: AppDispatch, _getState: () => AppState, { settingsStorage }: MutableThunkState): void => {
    dispatch(TutorialAction.setEnabled(false));
    dispatch(TutorialAction.setCompleted(true));
    dispatch(TutorialAction.setDismissed(false));
    dispatch(TutorialAction.setStep('completed'));
    dispatch(TutorialAction.setAttemptsUsed(0));
    dispatch(resetEncounterState());

    settingsStorage.setTutorialCompleted(true);
    settingsStorage.setTutorialDismissed(false);
    persistStep(settingsStorage, 'completed');
  };

const onPanelOpened =
  (panelType: PanelType): AppThunkAction =>
  (): void => {
    void panelType;
    // The reworked tutorial chapter no longer uses panels.
  };

const onDiceSelectionChanged =
  (dicesToRoll: Immutable.Map<DiceType, number>): AppThunkAction =>
  (): void => {
    void dicesToRoll;
    // Dice selection is hidden during this tutorial chapter.
  };

const onRollModifierSelected =
  (rollModifier: AbilityType | undefined): AppThunkAction =>
  (dispatch: AppDispatch, getState: () => AppState): void => {
    const { tutorial } = getState();
    if (!tutorial.enabled || !rollModifier) {
      return;
    }

    if (tutorial.step === 'select_strength_modifier' && rollModifier === AbilityType.STR) {
      dispatch(setStep('clear_log_with_strength'));
      return;
    }

    if (tutorial.step === 'select_dexterity_modifier' && rollModifier === AbilityType.DEX) {
      dispatch(setStep('cross_rope_with_dexterity'));
    }
  };

const onRollCompleted =
  (rollResults: RollResult[] | undefined, source: RollSource): AppThunkAction =>
  (dispatch: AppDispatch, getState: () => AppState): void => {
    if (source === 'init' || !rollResults) {
      return;
    }

    const state = getState();
    const { tutorial } = state;
    if (!tutorial.enabled) {
      return;
    }

    if (tutorial.step === 'tap_to_roll' && isSingleDiceRoll(rollResults, DiceType.d20)) {
      dispatch(setStep('select_strength_modifier'));
      return;
    }

    if (tutorial.step === 'clear_log_with_strength') {
      handleSkillCheckRoll(dispatch, state, rollResults, AbilityType.STR, LOG_TARGET, 'select_dexterity_modifier');
      return;
    }

    if (tutorial.step === 'cross_rope_with_dexterity') {
      handleSkillCheckRoll(dispatch, state, rollResults, AbilityType.DEX, ROPE_TARGET, 'defeat_tutorial_enemy');
      return;
    }

    if (tutorial.step === 'defeat_tutorial_enemy') {
      handleTutorialCombatRoll(dispatch, state, tutorial, rollResults);
      return;
    }
  };

const onAbilityValueChanged =
  (previousValue: number, newValue: number): AppThunkAction =>
  (): void => {
    void previousValue;
    void newValue;
    // Character editing is not part of this tutorial chapter.
  };

const handleSkillCheckRoll = (
  dispatch: AppDispatch,
  state: AppState,
  rollResults: RollResult[],
  requiredAbility: AbilityType,
  target: number,
  nextStep: TutorialStep,
): void => {
  const {
    dices: { rollModifier },
    character: { abilities, characterInfo },
  } = state;

  if (!isSingleDiceRoll(rollResults, DiceType.d20) || rollModifier !== requiredAbility) {
    return;
  }

  const rawRoll = rollResults[0].value;
  const baseAbility = abilities.get(requiredAbility) || 0;
  const raceModifier = getRaceModificator(characterInfo.race, requiredAbility);
  const total = rawRoll + getAbilityMod(baseAbility + raceModifier);

  if (total >= target) {
    dispatch(setEncounterState({ checkOutcome: 'success', lastCheckTotal: total }));
    dispatch(DiceAction.setRollModifier(undefined));
    dispatch(DiceAction.setRollResults(undefined));

    if (nextStep === 'defeat_tutorial_enemy') {
      dispatch(resetEncounterState());
      dispatch(setTutorialRollModifier(AbilityType.DEX));
      dispatch(DiceAction.setDicesToRoll(tutorialSingleD20Selection));
      dispatch(DiceAction.setRollResults(undefined));
      dispatch(setStep('defeat_tutorial_enemy'));
    } else {
      window.setTimeout(() => {
        dispatch(DiceAction.setRollModifier(undefined));
        dispatch(setStep(nextStep));
      }, 450);
    }
  } else {
    dispatch(setEncounterState({ checkOutcome: 'failure', lastCheckTotal: total }));
  }
};

const handleTutorialCombatRoll = (
  dispatch: AppDispatch,
  state: AppState,
  tutorial: AppState['tutorial'],
  rollResults: RollResult[],
): void => {
  const {
    character: { abilities, characterInfo },
  } = state;

  if (tutorial.combatOutcome === 'victory') {
    return;
  }

  if (tutorial.combatPhase === 'aim') {
    if (!isSingleDiceRoll(rollResults, DiceType.d20)) {
      dispatch(DiceAction.setDicesToRoll(tutorialSingleD20Selection));
      return;
    }

    const attackRoll = getRollTotalForAbility(rollResults[0].value, AbilityType.DEX, abilities, characterInfo.race);

    if (attackRoll > TUTORIAL_HIT_TARGET) {
      dispatch(setEncounterState({ combatPhase: 'damage', combatOutcome: 'hit', lastCombatRoll: attackRoll }));
      dispatch(setTutorialRollModifier(AbilityType.STR));
      dispatch(DiceAction.setDicesToRoll(tutorialSingleD6Selection));
    } else {
      dispatch(setEncounterState({ combatPhase: 'aim', combatOutcome: 'miss', lastCombatRoll: attackRoll }));
      dispatch(setTutorialRollModifier(AbilityType.DEX));
      dispatch(DiceAction.setDicesToRoll(tutorialSingleD20Selection));
    }

    return;
  }

  if (!isSingleDiceRoll(rollResults, DiceType.d6)) {
    dispatch(DiceAction.setDicesToRoll(tutorialSingleD6Selection));
    return;
  }

  const damageRoll = Math.max(
    1,
    getRollTotalForAbility(rollResults[0].value, AbilityType.STR, abilities, characterInfo.race),
  );
  const nextEnemyHp = Math.max(0, tutorial.enemyHp - damageRoll);

  if (nextEnemyHp <= 0) {
    dispatch(
      setEncounterState({
        enemyHp: 0,
        combatPhase: 'aim',
        combatOutcome: 'victory',
        lastCombatRoll: damageRoll,
      }),
    );
    dispatch(setTutorialRollModifier(AbilityType.DEX));
    dispatch(DiceAction.setDicesToRoll(tutorialSingleD20Selection));

    window.setTimeout(() => {
      dispatch(completeTutorial());
    }, VICTORY_ADVANCE_DELAY_MS);
  } else {
    dispatch(
      setEncounterState({
        enemyHp: nextEnemyHp,
        combatPhase: 'aim',
        combatOutcome: 'damage',
        lastCombatRoll: damageRoll,
      }),
    );
    dispatch(setTutorialRollModifier(AbilityType.DEX));
    dispatch(DiceAction.setDicesToRoll(tutorialSingleD20Selection));
  }
};

const getRollTotalForAbility = (
  rawRoll: number,
  abilityType: AbilityType,
  abilities: AppState['character']['abilities'],
  race: AppState['character']['characterInfo']['race'],
): number => {
  const baseAbility = abilities.get(abilityType) || 0;
  const raceModifier = getRaceModificator(race, abilityType);
  return rawRoll + getAbilityMod(baseAbility + raceModifier);
};

const isSingleDiceRoll = (rollResults: RollResult[], diceType: DiceType): boolean =>
  rollResults.length === 1 && rollResults[0].diceType === diceType;

export const TutorialThunk = {
  setStep,
  setAttemptsUsed,
  skipTutorial,
  restartTutorial,
  completeTutorial,
  onPanelOpened,
  onDiceSelectionChanged,
  onRollModifierSelected,
  onRollCompleted,
  onAbilityValueChanged,
};
